import React, { useCallback, useMemo, useState } from "react";
import { getCurrentDateClient } from "~/cores/appCore.js";
import TurnrLink from "~/components/links/TurnrLink";
import { newFeatures } from "~/newFeatures";
import styles from "./page.module.scss";
import GraphCardWithTitle from "./GraphCardWithTitle";
import { isAfter, parseISO, subDays } from "date-fns";
import classNames from "classnames";
import ClickableModalImage from "~/components/ClickableModalImage.jsx";
import { TurnrActionAlert } from "~/components/alerts/TurnrAlert.jsx";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { useBrowserSessionAndLocalState } from "rylak-common/src/hooks/useBrowserState.jsx";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { COLORS } from "rylak-common/src/constants.js";

const NEW_FEATURE_CUTOFF_DAYS = 14;

// Shared helpers for recent feature logic and stable keys
export function getRecentCutoff(days = NEW_FEATURE_CUTOFF_DAYS, currentDate) {
    return subDays(currentDate, days);
}

export function isRecentFeature(feature, referenceDate = getRecentCutoff()) {
    try {
        const featureDate = parseISO(feature?.date);
        return isAfter(featureDate, referenceDate);
    } catch (e) {
        return false;
    }
}

export function slugifyFeature(feature) {
    const base = `${feature?.date || ""}-${feature?.title || ""}`.toLowerCase();
    return base
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}

// Utility to check if there are any recent features (based on date only)
export function hasRecentFeatures(currentDate) {
    const cutoff = getRecentCutoff(NEW_FEATURE_CUTOFF_DAYS, currentDate);
    return newFeatures.some((f) => isRecentFeature(f, cutoff));
}

// Reusable, dismissible alerts for recent features
export function RecentFeatureAlerts({ appProps }) {
    const currentDate = getCurrentDateClient(appProps);
    const cutoff = useMemo(() => getRecentCutoff(NEW_FEATURE_CUTOFF_DAYS, currentDate), [currentDate]);
    const recent = useMemo(() => newFeatures.filter((f) => isRecentFeature(f, cutoff)), [cutoff]);

    const alerts = recent
        .map((feature) => {
            const slug = slugifyFeature(feature);
            const storageKey = `metabaseRecentFeatureSeen:${slug}`;
            const [seen, setSeen, initialized] = useBrowserSessionAndLocalState({ key: storageKey, defaultValue: false, initWithDefaultValue: true, timeInHours: NEW_FEATURE_CUTOFF_DAYS * 24 });

            if (!initialized || seen) return null;

            return (
                <TurnrActionAlert key={slug} fullWidth={true} title={<b>New feature: {feature?.title}</b>} onClose={() => setSeen(true)}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            gap: 12,
                            width: "100%",
                            flexWrap: "wrap",
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, flex: 1 }}>
                            <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
                                {feature?.description && <div style={{ whiteSpace: "pre-wrap" }}>{feature.description}</div>}

                                {feature?.imageUrl && (
                                    <div
                                        style={{
                                            marginTop: "16px",
                                            position: "relative",
                                            width: "fit-content",
                                        }}
                                    >
                                        <ClickableModalImage imageUrl={feature?.imageUrl} maxWidth={250} modalFixedWidth={1000} />
                                    </div>
                                )}
                            </div>
                            {feature?.link && (
                                <div>
                                    <TurnrButton color={COLORS.LINK} href={feature.link} size={"small"} variant={"text"}>
                                        Go to feature
                                    </TurnrButton>
                                    {/*<TurnrLink href={feature.link}>Go to feature</TurnrLink>*/}
                                </div>
                            )}
                        </div>
                    </div>
                </TurnrActionAlert>
            );
        })
        .filter(Boolean);

    if (alerts.length === 0) return null;

    return <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>{alerts}</div>;
}

// Grid-wrapped version for pages that need the grid item but should render nothing when no alerts are visible
export function RecentFeatureAlertsRow({ appProps }) {
    const currentDate = getCurrentDateClient(appProps);
    const cutoff = useMemo(() => getRecentCutoff(NEW_FEATURE_CUTOFF_DAYS, currentDate), [currentDate]);
    const recent = useMemo(() => newFeatures.filter((f) => isRecentFeature(f, cutoff)), [cutoff]);

    // Build once to know if there is at least one visible alert (not dismissed and initialized)
    const alertNodes = recent
        .map((feature) => {
            const slug = slugifyFeature(feature);
            const storageKey = `metabaseRecentFeatureSeen:${slug}`;
            const [seen, , initialized] = useBrowserSessionAndLocalState({ key: storageKey, defaultValue: false, initWithDefaultValue: true, timeInHours: NEW_FEATURE_CUTOFF_DAYS * 24 });
            if (!initialized || seen) return null;
            // Return a simple marker; we'll render real alerts via RecentFeatureAlerts below
            return true;
        })
        .filter(Boolean);

    if (alertNodes.length === 0) return null;

    return (
        <TurnrGrid item xs={12} xl={9}>
            <RecentFeatureAlerts appProps={appProps} />
        </TurnrGrid>
    );
}

function NewFeaturesCard({ appProps }) {
    const currentDate = getCurrentDateClient(appProps);
    const twoWeeksAgo = useMemo(() => getRecentCutoff(NEW_FEATURE_CUTOFF_DAYS, currentDate), [currentDate]);

    const initialVisibleCount = useMemo(() => {
        const recentFeatures = newFeatures.filter((feature) => isRecentFeature(feature, twoWeeksAgo));
        return Math.max(recentFeatures?.length, 3);
    }, [twoWeeksAgo]);

    const isRecentFeatureLocal = useCallback((feature) => isRecentFeature(feature, twoWeeksAgo), [twoWeeksAgo]);
    const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
    const [expandedItems, setExpandedItems] = useState({});

    const handleShowMore = () => {
        setVisibleCount((prevCount) => prevCount + 10);
    };

    const toggleItemExpansion = (index) => {
        setExpandedItems((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const visibleFeatures = newFeatures.slice(0, visibleCount);
    const hasMoreFeatures = visibleCount < newFeatures.length;

    return (
        <GraphCardWithTitle height={"fit-content"} divider={true}>
            <div className={styles["new-features-list"]}>
                {visibleFeatures.map((feature, index) => {
                    const expanded = expandedItems[index];

                    return (
                        <div key={index}>
                            <div
                                onClick={() => toggleItemExpansion(index)}
                                className={classNames(styles["new-feature-item"], {
                                    [styles["new-feature-item-expanded"]]: expandedItems[index],
                                })}
                                style={{ cursor: "pointer", display: !expanded && "flex" }}
                            >
                                <div
                                    className={styles["new-feature-header-container"]}
                                    style={{
                                        display: "flex",
                                        gap: "5px",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        whiteSpace: "pre",
                                        marginRight: "5px",
                                        maxWidth: "100%",
                                        width: "fit-content",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                            marginBottom: "5px",
                                            maxWidth: "100%",
                                            width: "fit-content",
                                        }}
                                    >
                                        <div className={styles["new-feature-date"]}>{feature.date}</div>

                                        <div className={styles["new-feature-title"]} style={{ maxWidth: "100%", width: "fit-content" }}>
                                            {feature.title}
                                            {isRecentFeatureLocal(feature) && <span className={styles["new-feature-recent-badge"]}>NEW</span>}
                                        </div>
                                    </div>
                                </div>
                                {/*{expandedItems[index] ? (*/}
                                {/*    <>*/}
                                {/*        <div className={styles["new-feature-description"]}>{feature.description}</div>*/}
                                {/*        <div className={styles["new-feature-divider"]}></div>*/}
                                {/*    </>*/}
                                {/*) : (*/}
                                {/*    <div className={styles["new-feature-description-preview"]}>*/}
                                {/*        {feature.description.length > textLimit ? `${feature.description.substring(0, textLimit)}...` : feature.description}*/}
                                {/*    </div>*/}
                                {/*)}*/}

                                {expanded ? (
                                    <div className={styles["new-feature-description"]}>{feature.description}</div>
                                ) : (
                                    <div className={styles["new-feature-description-preview"]}>{feature.description}</div>
                                )}
                                {feature?.imageUrl && expanded && (
                                    <div
                                        style={{
                                            marginTop: "16px",
                                            position: "relative",
                                            width: "fit-content",
                                        }}
                                    >
                                        <ClickableModalImage imageUrl={feature?.imageUrl} maxWidth={300} />
                                    </div>
                                )}
                                {feature.link && expanded && (
                                    <div
                                        style={{ marginTop: "5px" }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                        }}
                                    >
                                        <TurnrLink
                                            key={index}
                                            href={feature.link}
                                            style={{
                                                fontSize: "12px",
                                            }}
                                            // Prevent toggling when clicking the link
                                        >
                                            Go to feature
                                        </TurnrLink>
                                    </div>
                                )}
                                {expanded && <div className={styles["new-feature-divider"]}></div>}
                            </div>
                        </div>
                    );
                })}
            </div>
            {hasMoreFeatures && (
                <div className={styles["show-more-container"]}>
                    <button className={styles["show-more-button"]} onClick={handleShowMore}>
                        Show more
                    </button>
                </div>
            )}
        </GraphCardWithTitle>
    );
}

export default NewFeaturesCard;
